import Dialog, { DialogProps } from "@components/Dialog"
import { useGraphDocument } from "@features/builder/GraphDocumentProvider/hooks/useGraphDocument"
import { GraphDetails } from "@features/builder/types"
import { Button } from "@mui/material"
import { stringToFile } from "@utils/file"
import { closeSnackbar, enqueueSnackbar } from "notistack"
import { updateGraphDocumentData } from "@utils/crdt"
import DeleteDialog from "@components/DeleteDialog"
import React, { useEffect, useState } from "react"
import {
  useDeleteValidationFileMutation,
  useGetGraphValidationFileQuery,
  usePostValidationFileMutation,
} from "@store/services/validationService"
import ValidationConfigurationForm, {
  ValidationConfigurationFormValues,
} from "@features/builder/ValidationConfiguration/ValidationConfigurationForm"
import logger from "@utils/logger"
import { FormProvider, useForm } from "react-hook-form"
import { yupResolver } from "@hookform/resolvers/yup"
import { validationSchema } from "@features/builder/ValidationConfiguration/helpers"
import ValidationErrorsSnackbar from "@features/builder/graph/validation/ValidationErrorsSnackbar"

const FORM_ID = "dashboard-configuration-form"

const getDefaultValues = (fileInString: string | undefined): { configuration: string } => {
  if (!fileInString) return { configuration: "" }
  return { configuration: fileInString }
}

type ValidationConfigurationDialogProps = Pick<DialogProps, "onClose"> & {
  graphKey: string
}

const ValidationConfigurationDialog = (props: ValidationConfigurationDialogProps) => {
  const [postValidationFile] = usePostValidationFileMutation()
  const [deleteValidationFile] = useDeleteValidationFileMutation()
  const { data: fetchedValidation } = useGetGraphValidationFileQuery(props.graphKey)
  const [errorMessages, setErrorMessages] = useState<string[]>([])
  const graphDocument = useGraphDocument()
  const [isDeleteOpen, setIsDeleteOpen] = useState(false)

  const form = useForm<ValidationConfigurationFormValues>({
    resolver: yupResolver(validationSchema),
    defaultValues: getDefaultValues(fetchedValidation),
  })

  useEffect(() => {
    form.reset(getDefaultValues(fetchedValidation))
  }, [fetchedValidation, form])

  const handleSubmit = async (values: ValidationConfigurationFormValues) => {
    closeSnackbar()
    if (!graphDocument) return

    if (values.configuration) {
      const file = stringToFile("validation.yaml", values.configuration)
      try {
        const response = await postValidationFile({
          data: file,
          graph_id: props.graphKey,
        }).unwrap()

        if (!("validation_hash" in response)) return

        const graphData = JSON.parse(
          graphDocument.getMap("graph").get("data")?.toJSON() ?? "null",
        ) as GraphDetails | null

        if (!graphData) {
          logger.error(`Graph data does not exist on graph "${props.graphKey}"`)
          return
        }

        graphData.ValidationDocument = response.path
        updateGraphDocumentData(graphDocument, graphData)

        enqueueSnackbar({
          variant: "success",
          message: "Sampling Validation document saved successfully",
        })
      } catch (e) {
        const errors = (e as { data: { message: string } }).data.message.split("\n")
        setErrorMessages(errors)

        enqueueSnackbar("Validation errors", {
          persist: true,
          anchorOrigin: { horizontal: "right", vertical: "top" },
          content: (key) => (
            <ValidationErrorsSnackbar
              id={key}
              errors={errors.map((error) => error.split("VALIDATION_DOCUMENT_ERROR:")[1] ?? "")}
              title="Validation Errors"
            />
          ),
        })
        return
      }
    }
  }

  const onDeleteClickHandler = async () => {
    if (!graphDocument) return

    try {
      await deleteValidationFile(props.graphKey).unwrap()

      // remove from the CRDT doc
      const graphData = JSON.parse(
        graphDocument.getMap("graph").get("data")?.toJSON() ?? "null",
      ) as GraphDetails | null

      if (!graphData) {
        logger.error(`Graph data does not exist on graph "${props.graphKey}"`)
        return
      }

      graphData.ValidationDocument = ""
      updateGraphDocumentData(graphDocument, graphData)

      // Clear form and error state
      form.reset({ configuration: "" })
      setErrorMessages([])

      enqueueSnackbar({
        variant: "success",
        message: "Sampling Validation document deleted successfully",
      })
      setIsDeleteOpen(false)
    } catch (e) {
      enqueueSnackbar({
        variant: "error",
        message: "Failed to delete validation document",
      })
      logger.error("Failed to delete validation document", e)
    }
  }

  const handleClear = async () => {
    setIsDeleteOpen(true)
  }

  return (
    <>
      <Dialog
        open
        onClose={props.onClose}
        title="Post-sampling result validation configuration"
        fullScreen
        actionsComponent={
          <>
            <Button type="submit" variant="contained" form={FORM_ID}>
              Save
            </Button>
          </>
        }
      >
        {props.graphKey && (
          <FormProvider {...form}>
            <ValidationConfigurationForm
              id={FORM_ID}
              onClear={handleClear}
              onSubmit={handleSubmit}
              errorMessages={errorMessages}
              graphKey={props.graphKey}
            />
          </FormProvider>
        )}
      </Dialog>
      <DeleteDialog
        open={isDeleteOpen}
        setOpen={setIsDeleteOpen}
        onDeleteClick={onDeleteClickHandler}
      />
    </>
  )
}

export default ValidationConfigurationDialog
